/**
 * Cloche Admin — inbox opérationnelle commerçant.
 * Polling léger (~45s) + refresh focus / actions.
 * Aucun son, aucun WebSocket.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';

const POLL_MS = 45000;

export function useAdminNotifications(enabled = true) {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async ({ silent = true } = {}) => {
    if (!enabled) {
      if (mountedRef.current) {
        setItems([]);
        setUnreadCount(0);
      }
      return;
    }
    try {
      if (!silent && mountedRef.current) setLoading(true);
      const data = await apiService.getAdminNotifications({ limit: 20 });
      if (!mountedRef.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setUnreadCount(Number(data?.unreadCount) || 0);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || 'Erreur notifications');
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [enabled]);

  const refreshCount = useCallback(async () => {
    if (!enabled) {
      if (mountedRef.current) setUnreadCount(0);
      return;
    }
    try {
      const data = await apiService.getAdminNotificationsUnreadCount();
      if (mountedRef.current) {
        setUnreadCount(Number(data?.unreadCount) || 0);
      }
    } catch {
      // Badge optionnel — silencieux
    }
  }, [enabled]);

  const markRead = useCallback(async (id) => {
    if (!id) return;
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await apiService.markAdminNotificationRead(id);
      await refreshCount();
    } catch {
      await refresh({ silent: true });
    }
  }, [refresh, refreshCount]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await apiService.markAllAdminNotificationsRead();
      await refresh({ silent: true });
    } catch {
      await refresh({ silent: true });
    }
  }, [refresh]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setUnreadCount(0);
      return undefined;
    }

    refresh({ silent: false });
    const interval = setInterval(() => refreshCount(), POLL_MS);
    const onFocus = () => refresh({ silent: true });
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, refresh, refreshCount]);

  return {
    items,
    unreadCount,
    loading,
    error,
    refresh,
    markRead,
    markAllRead,
  };
}

export default useAdminNotifications;
